import { useState } from 'react';
import { Clock, MapPin, Pencil, Calendar, CirclePlus } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  getDateWithTimeZone,
  getTimeWithTimeZone
} from '@/helpers/dateHelpers';
import { getUserInitials } from '@/helpers/stringHelpers';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useSelfCoachBookingPage } from '@/data/booking';

import EditNoteForm from './EditNoteForm';

const NextBooking = ({ booking }) => {
  const [isEditingNote, setIsEditingNote] = useState(false);
  const coachBookings = useSelfCoachBookingPage();

  const handleEditNotesSuccess = async () => {
    await coachBookings.mutate();
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader className="bg-muted text-white p-4 text-center py-1 rounded-t-md">
        <h2 className="text-md font-semibold">
          {getDateWithTimeZone(
            booking.startDateTime,
            booking.timeZone,
            'EEEE MMM d, yyyy'
          )}
        </h2>
      </CardHeader>
      <CardContent className="px-2 py-4">
        <div className="flex items-start gap-2 mb-6">
          <Avatar className="h-14 w-14">
            <AvatarImage
              src={`/avatars/${booking.coachId}.jpg`}
              alt={booking.user?.firstname}
            />
            <AvatarFallback>{getUserInitials(booking.user)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <p className="text-muted-foreground mb-1 text-xs">
              {booking.activityTitle} with
            </p>
            <h3 className="text-sm font-semibold mb-2">
              {booking.userFirstname} {booking.userLastname}
            </h3>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs">{booking.locationName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs">
                {getDateWithTimeZone(booking.startDateTime, booking.timeZone)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs">
                {getTimeWithTimeZone(booking.startDateTime, booking.timeZone)} -{' '}
                {getTimeWithTimeZone(booking.endDateTime, booking.timeZone)}
              </span>
            </div>
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-semibold">Notes</h4>
            {!isEditingNote && booking.coachNotes && (
              <Button variant="ghost" size="icon" className="h-6 w-6">
                <Pencil
                  className="h-4 w-4"
                  onClick={() => setIsEditingNote(true)}
                />
              </Button>
            )}
          </div>
          {!isEditingNote && booking.coachNotes && (
            <p className="text-muted-foreground text-sm h-24 overflow-y-auto">
              {booking.coachNotes}
            </p>
          )}
          {!isEditingNote && !booking.coachNotes && (
            <div className="flex flex-col h-24 justify-center items-center">
              <Button
                variant="ghost"
                size="sm"
                className="mt-2"
                onClick={() => setIsEditingNote(true)}
              >
                <CirclePlus />
                &nbsp;Add Note
              </Button>
            </div>
          )}
          {isEditingNote && (
            <EditNoteForm
              booking={booking}
              setIsEditingNote={setIsEditingNote}
              onSuccess={handleEditNotesSuccess}
              key={booking.coachNotes}
            />
          )}
        </div>

        {!isEditingNote && (
          <div className="flex justify-between items-center mt-6">
            {booking.conversation && (
              <Button variant="outline" size="sm" asChild>
                <Link to={`/conversation/${booking.conversation.id}`}>
                  Contact {booking.userFirstname}
                </Link>
              </Button>
            )}
            <Button variant="outline" size="sm" asChild>
              <Link to={`/booking/${booking.id}/coach`}>View</Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NextBooking;
